"use client";

import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@/components/TurnstileWidget";
import { trackGenerateLead } from "@/lib/analytics";
// Opening hours come from the canonical NAP source (lib/brand.ts) — never
// hardcoded, so a change there propagates to every surface at once.
import { brand } from "@/lib/brand";

/**
 * Structured bespoke-order enquiry form (WC1).
 *
 * The /enquire page used to be channels-only — a visitor who didn't click
 * through to WhatsApp/IG was lost. This captures the lead (occasion, date,
 * budget, optional reference image) into D1 via POST /api/enquiries so it's
 * actionable in the admin, while the channel cards below stay as the "or
 * reach us directly" secondary path.
 */

const OCCASIONS = [
  "Birthday",
  "Anniversary",
  "Wedding",
  "Sympathy / Funeral",
  "Get Well",
  "New Baby",
  "Congratulations",
  "Thank You",
  "Just Because",
  "Corporate / Event",
  "Other",
];

const BUDGETS = [
  "Under £30",
  "£30–£50",
  "£50–£80",
  "£80–£120",
  "£120+",
  "Flexible / not sure",
];

const CHANNELS: { value: string; label: string }[] = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone call" },
  { value: "instagram", label: "Instagram" },
  { value: "wechat", label: "WeChat" },
];

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
/**
 * Keep the client in LOCK-STEP with the server contract. `POST /api/enquiries`
 * only verifies Turnstile when `TURNSTILE_SECRET_KEY` + the site key are set
 * AND `NODE_ENV === "production"` (see `turnstileConfigured` there) — exactly
 * the condition `CheckoutClient`'s `turnstileEnabled` and `LoginClient` use.
 *
 * Gating on the site key ALONE made the client stricter than the server: the
 * key is public and non-secret, so any non-production build that has it set
 * (local dev, the e2e harness, a preview deploy) rendered "Send enquiry"
 * permanently `disabled` — a dead lead-capture form with no error shown, while
 * the server would happily have accepted the enquiry without a token.
 */
const TURNSTILE_REQUIRED =
  !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && process.env.NODE_ENV === "production";

const selectClass =
  "flex min-h-11 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-hidden focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

export function EnquiryForm({ bare = false }: { bare?: boolean } = {}) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // A challenge that errors or times out must not dead-end the lead: without a
  // token the submit button stays disabled forever, so surface a retry the way
  // checkout does rather than leaving the visitor staring at a dead button.
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  // Turnstile tokens are single-use; refresh after any failed submit so a retry
  // isn't rejected as a duplicate.
  const refreshTurnstile = () => {
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  };
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(typeof reader.result === "string" ? reader.result : null);
      setImageName(file.name);
    };
    reader.onerror = () => toast.error("Couldn't read that image.");
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageDataUrl(null);
    setImageName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (TURNSTILE_REQUIRED && !turnstileToken) {
      toast.error("Please complete the security check before sending your enquiry.");
      return;
    }
    const form = e.currentTarget;
    const data = new FormData(form);

    const payload = {
      name: String(data.get("name") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      phone: String(data.get("phone") ?? "").trim(),
      occasion: String(data.get("occasion") ?? "").trim(),
      dateNeeded: String(data.get("dateNeeded") ?? "").trim(),
      budget: String(data.get("budget") ?? "").trim(),
      preferredChannel: String(data.get("preferredChannel") ?? "").trim(),
      message: String(data.get("message") ?? "").trim(),
      ...(imageDataUrl ? { imageDataUrl } : {}),
      ...(turnstileToken ? { turnstileToken } : {}),
    };

    if (!payload.name) {
      toast.error("Please tell us your name.");
      return;
    }
    if (!payload.email && !payload.phone) {
      toast.error("Please leave an email or phone number so we can reply.");
      return;
    }
    if (!payload.message) {
      toast.error("Please tell us a little about what you're after.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/enquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        toast.error(json.error ?? "Something went wrong. Please try a channel below.");
        refreshTurnstile();
        return;
      }
      setDone(true);
      trackGenerateLead(payload.preferredChannel || "enquiry_form");
      toast.success("Thank you! We'll be in touch soon.");
    } catch {
      toast.error("Network error. Please try a channel below.");
      refreshTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className={
          bare
            ? "text-center"
            : "mx-auto max-w-2xl rounded-2xl border border-primary/30 bg-card p-8 text-center shadow-sm"
        }
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-6 w-6 text-primary" aria-hidden="true" />
        </div>
        <h3 className="mt-4 heading-card">Enquiry sent</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Thank you for reaching out. We read every message personally and will reply
          within hours during our hours ({brand.hours.display}). Prefer something faster?
          Message us on WhatsApp below.
        </p>
        <Button variant="outline" className="mt-6" onClick={() => setDone(false)}>
          Send another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={
        bare
          ? ""
          : "mx-auto max-w-2xl rounded-2xl border border-border/60 bg-card p-6 shadow-sm sm:p-8"
      }
      aria-label="Bespoke flower enquiry form"
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-1">
          <Label htmlFor="enq-name">Your name <span className="text-destructive">*</span></Label>
          <Input
            id="enq-name"
            name="name"
            required
            autoComplete="name"
            maxLength={200}
            className="mt-1.5 min-h-11"
            placeholder="Jane Smith"
          />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="enq-email">Email</Label>
          <Input
            id="enq-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={254}
            className="mt-1.5 min-h-11"
            placeholder="jane@example.com"
          />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="enq-phone">Phone</Label>
          <Input
            id="enq-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={40}
            className="mt-1.5 min-h-11"
            placeholder="07123 456789"
          />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="enq-channel">Best way to reach you</Label>
          <select id="enq-channel" name="preferredChannel" className={`mt-1.5 ${selectClass}`} defaultValue="">
            <option value="">No preference</option>
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="enq-occasion">Occasion</Label>
          <select id="enq-occasion" name="occasion" className={`mt-1.5 ${selectClass}`} defaultValue="">
            <option value="">Select an occasion…</option>
            {OCCASIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="enq-budget">Budget</Label>
          <select id="enq-budget" name="budget" className={`mt-1.5 ${selectClass}`} defaultValue="">
            <option value="">Select a budget…</option>
            {BUDGETS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="enq-date">Date needed</Label>
          <Input
            id="enq-date"
            name="dateNeeded"
            type="date"
            className="mt-1.5 min-h-11"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="enq-message">
            Tell us what you have in mind <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="enq-message"
            name="message"
            required
            rows={5}
            maxLength={4000}
            className="mt-1.5"
            placeholder="Colours, style, who it's for, where it's going… or just the occasion — we'll guide you from there."
          />
        </div>

        <div className="sm:col-span-2">
          <Label>Reference image (optional)</Label>
          <div className="mt-1.5">
            {imageName ? (
              <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                <span className="truncate text-muted-foreground">{imageName}</span>
                <button
                  type="button"
                  onClick={clearImage}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center text-muted-foreground hover:text-foreground"
                  aria-label="Remove image"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="enq-image"
                className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                Add a mood-board or inspiration photo
              </label>
            )}
            <input
              ref={fileInputRef}
              id="enq-image"
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={handleFile}
            />
          </div>
        </div>
      </div>

      {TURNSTILE_REQUIRED && (
        <div className="mt-5">
          <TurnstileWidget
            ref={turnstileRef}
            onVerify={(t) => {
              setTurnstileToken(t);
              setTurnstileError(false);
            }}
            onExpire={() => setTurnstileToken(null)}
            onError={() => {
              setTurnstileToken(null);
              setTurnstileError(true);
            }}
          />
          {turnstileError && (
            <div
              role="alert"
              className="mt-2 flex flex-col items-start gap-1.5 text-sm text-muted-foreground"
            >
              <p>
                The &ldquo;I&apos;m human&rdquo; check didn&apos;t complete. This is usually
                temporary — please try again.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTurnstileError(false);
                  refreshTurnstile();
                }}
              >
                Try the check again
              </Button>
            </div>
          )}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={submitting || (TURNSTILE_REQUIRED && !turnstileToken)}
        className="mt-5 w-full sm:w-auto sm:min-w-56"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" aria-hidden="true" />
            Send enquiry
          </>
        )}
      </Button>
      <p className="mt-3 text-xs text-muted-foreground">
        We&apos;ll only use your details to reply to this enquiry. No spam, ever.
      </p>
    </form>
  );
}
